import React from 'react';
import TruncateMarkup from 'react-truncate-markup';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Link from 'src/components/ui/Link';
import Avatar from 'src/components/ui/Avatar';
import Typography from 'src/components/ui/Typography';
import Image from 'src/components/ui/Image';

import { colours } from 'src/constants/colours';

import type { Medium, StoryReactionsQuery, User } from 'src/types/__generated__/graphql';
import useActivityToDisplay from 'src/hooks/useActivityToDisplay';
import { routePathContactsWithUserId } from 'src/constants/router';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledBox = styledComponent(Box)`
    height: 6rem;
    width: 23.75rem;
    border-bottom: 0.063rem solid ${colours.primaryOpacity.O12};
`;

const StyledBoxArea = styledComponent(Box)`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: row;
`;

const StyledAvatar = styledComponent(Avatar)`
    margin-top: 1.2rem;
`;

const ReactionMessage = styledTag('div')`
    width: 2.5rem;
    height: 4rem;
    margin-left: 4rem;
    margin-top: 0.5rem;
    display: flex;
    grid-template-columns: repeat(3, minmax(3rem, 1fr));
    img {
        background-color: transparent;
        height: 24rem;
        width: 18rem;
    }
`;

const StyledLink = styledComponent(Link)<{ $isWithReaction?: boolean }>`
    ${({ $isWithReaction }) => ($isWithReaction ? `margin-left: 2rem;` : `margin-left: 8.5rem;`)}
    margin-top: 2rem;
    color: ${colours.secondary.main};
    text-decoration: unset;
    font-size: 0.875rem;
`;

interface Props {
    storyReaction?: ArrayElement<StoryReactionsQuery['storyReactions']>;
}

const StoryReactionCard: React.FC<Props> = ({ storyReaction }) => {
    const { t } = useTranslation(['common']);

    const user = storyReaction?.user;
    const message = storyReaction?.message;
    const reaction = storyReaction?.reaction;
    const getActivityToDisplay = useActivityToDisplay();

    return (
        <StyledBox>
            <StyledBoxArea>
                <StyledAvatar
                    data-target-id="avatar"
                    alt={user?.fullName}
                    image={user?.image as Medium}
                    fallbackText={user?.initials}
                    size={40}
                />
                <Box display="flex" flexDirection="column" ml="1rem" width="7rem">
                    <Box mt="1rem">
                        <Typography variant="h5" data-target-id="fullName" noWrap>
                            {user?.fullName}
                        </Typography>

                        <Box mt="0.1rem">
                            <Typography data-target-id="activity" variant="subtitle2" noWrap>
                                {getActivityToDisplay(user as User)}
                            </Typography>
                        </Box>
                        {message && (
                            <Box width="15.5rem" mb="1rem" mt="0.3rem">
                                <TruncateMarkup lines={1}>
                                    <view data-target-id="content"> {message} </view>
                                </TruncateMarkup>
                            </Box>
                        )}
                    </Box>
                </Box>
                {reaction && (
                    <ReactionMessage>
                        <Image
                            data-target-id="reaction"
                            src={`${process.env.NEXT_PUBLIC_SITE_URL}/assets/images/stories/reactions/woopy-${reaction}.png`}
                            alt={`woopy-${reaction}`}
                            height={100}
                            width={100}
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                    </ReactionMessage>
                )}
                <StyledLink
                    data-target-id="answer"
                    $isWithReaction={Boolean(reaction)}
                    href={`${routePathContactsWithUserId}${user?.id}`}
                >
                    {t('common:answer')}
                </StyledLink>
            </StyledBoxArea>
        </StyledBox>
    );
};

export default StoryReactionCard;
